import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import type { FinProject, FinProjectsPayload, FundingSource, ProjectCandidate, ProjectKind, ProjectStatus } from '../../lib/financeTypes';
import { EmptyState, Field, LoadingPage, Tag } from '../../components/ui';
import { Metric, usd } from './shared';

const KIND_LABEL: Record<string, string> = {
  'pool-repair': 'Pool repair',
  'capital-improvement': 'Capital improvement',
  'emergency-repair': 'Emergency repair',
  'major-repair': 'Major repair',
  'landscape-project': 'Major landscape project',
  'insurance-deductible': 'Insurance deductible',
  'reserve-study-item': 'Reserve-funded project',
  other: 'Other exceptional expense',
};
const FUNDING_LABEL: Record<string, string> = {
  operating: 'Operating fund',
  reserves: 'Reserves',
  'special-assessment': 'Special assessment',
  insurance: 'Insurance proceeds',
  loan: 'Loan',
  mixed: 'Mixed',
  other: 'Other',
};
const STATUS_LABEL: Record<ProjectStatus, string> = {
  planned: 'Planned',
  'in-progress': 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};
const STATUS_TONE: Record<ProjectStatus, 'neutral' | 'info' | 'ok' | 'bad'> = {
  planned: 'neutral',
  'in-progress': 'info',
  completed: 'ok',
  cancelled: 'bad',
};

export function Projects() {
  const { notify, fail } = useToast();
  const state = useAsync(() => api.finProjects(), []);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const data = state.data;

  if (!data) return state.loading ? <LoadingPage label="Loading projects…" /> : null;

  const open = data.projects.filter((p) => p.status === 'planned' || p.status === 'in-progress');
  const totals = data.totals;
  const apply = (next: FinProjectsPayload) => state.setData(next);
  async function guard<T>(work: () => Promise<T>, ok: string, then?: (v: T) => void) {
    try {
      then?.(await work());
      notify(ok, 'success');
    } catch (error) {
      fail(error, 'That did not work.');
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-head">
          <h3>Special expenses and projects</h3>
          <button type="button" className="btn" onClick={() => setAdding((v) => !v)}>{adding ? 'Close' : 'Add a project'}</button>
        </div>
        <p className="mut">
          Track pool repairs, capital improvements, emergency repairs, major landscape projects, insurance deductibles, reserve-funded work and other exceptional expenses separately from everyday operating costs.
        </p>
        <div className="stat-grid">
          <Metric label="Open projects" value={open.length} />
          <Metric label="Budget (open)" value={usd(totals?.budget ?? open.reduce((t, p) => t + (p.budget ?? 0), 0))} />
          <Metric label="Actual spending" value={usd(totals?.spent ?? data.projects.reduce((t, p) => t + p.spent, 0))} hint="Linked ledger items are already in statement expenses and are not added again" />
          <Metric label="Planned payments remaining" value={usd(totals?.plannedPaymentsRemaining ?? 0)} hint="Future commitments. Not actual spending." />
          <Metric label="Special assessments outstanding" value={usd(totals?.assessmentOutstanding ?? 0)} />
        </div>
        {data.notes?.map((n) => <p className="mut" key={n} style={{ margin: '4px 0 0' }}>{n}</p>)}
      </div>

      {adding && (
        <ProjectForm
          kinds={data.kinds}
          fundingSources={data.fundingSources ?? (Object.keys(FUNDING_LABEL) as FundingSource[])}
          onSave={(input) => guard(() => api.finProjectSave(input), 'Project added.', (next) => { apply(next); setAdding(false); })}
        />
      )}

      {data.projects.length === 0 ? (
        <EmptyState title="No projects yet." hint="Add the first one above." />
      ) : (
        data.projects.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            kinds={data.kinds}
            fundingSources={data.fundingSources ?? (Object.keys(FUNDING_LABEL) as FundingSource[])}
            open={openId === p.id}
            onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            onSave={(input) => guard(() => api.finProjectSave(input, p.id), 'Project saved.', apply)}
            onDelete={() => {
              if (window.confirm(`Delete “${p.name}” and its recorded spending?`)) void guard(() => api.finProjectDelete(p.id), 'Project deleted.', (next) => { apply(next); setOpenId(null); });
            }}
            onExpense={(input) => guard(() => api.finProjectExpense(p.id, input), 'Spending recorded.', apply)}
            onExpenseDelete={(expenseId) => guard(() => api.finProjectExpenseDelete(p.id, expenseId), 'Removed.', apply)}
            onSchedule={(input) => guard(() => api.finProjectSchedule(p.id, input), 'Payment date added.', apply)}
            onScheduleDone={(itemId) => guard(() => api.finProjectScheduleDone(p.id, itemId), 'Updated.', apply)}
            onScheduleDelete={(itemId) => guard(() => api.finProjectScheduleDelete(p.id, itemId), 'Removed.', apply)}
          />
        ))
      )}
    </div>
  );
}

function ProjectForm({
  kinds,
  fundingSources,
  project,
  onSave,
}: {
  kinds: ProjectKind[];
  fundingSources: FundingSource[];
  project?: FinProject;
  onSave: (input: Record<string, unknown>) => void | Promise<void>;
}) {
  const [name, setName] = useState(project?.name ?? '');
  const [kind, setKind] = useState<ProjectKind>(project?.kind ?? 'capital-improvement');
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? 'planned');
  const [fund, setFund] = useState(project?.fund ?? 'replacement');
  const [fundingSource, setFundingSource] = useState<string>(project?.fundingSource ?? 'reserves');
  const [description, setDescription] = useState(project?.description ?? '');
  const [budget, setBudget] = useState(project?.budget?.toString() ?? '');
  const [levied, setLevied] = useState(project?.specialAssessment?.levied ? String(project.specialAssessment.levied) : '');
  const [collected, setCollected] = useState(project?.specialAssessment?.collected ? String(project.specialAssessment.collected) : '');
  const [startsOn, setStartsOn] = useState(project?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(project?.endsOn ?? '');
  const [vendorName, setVendorName] = useState(project?.vendorName ?? '');
  const [notes, setNotes] = useState(project?.notes ?? '');

  return (
    <div className="card">
      <div className="grid two">
        <Field label="Project name"><input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></Field>
        <Field label="Type">
          <select value={kind} onChange={(e) => setKind(e.target.value as ProjectKind)}>
            {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] ?? k}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
            {(Object.keys(STATUS_LABEL) as ProjectStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </Field>
        <Field label="Cash from (operating vs replacement)">
          <select value={fund} onChange={(e) => setFund(e.target.value as 'operating' | 'replacement')}>
            <option value="replacement">Replacement (reserve) fund</option>
            <option value="operating">Operating fund</option>
          </select>
        </Field>
        <Field label="Funding source">
          <select value={fundingSource} onChange={(e) => setFundingSource(e.target.value)}>
            {fundingSources.map((s) => <option key={s} value={s}>{FUNDING_LABEL[s] ?? s}</option>)}
          </select>
        </Field>
        <Field label="Budgeted cost ($)"><input value={budget} inputMode="decimal" onChange={(e) => setBudget(e.target.value)} /></Field>
        <Field label="Special assessment levied ($)"><input value={levied} inputMode="decimal" onChange={(e) => setLevied(e.target.value)} /></Field>
        <Field label="Special assessment collected ($)" hint="Outstanding is levied minus collected."><input value={collected} inputMode="decimal" onChange={(e) => setCollected(e.target.value)} /></Field>
        <Field label="Vendor"><input value={vendorName} onChange={(e) => setVendorName(e.target.value)} maxLength={120} /></Field>
        <Field label="Starts"><input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} /></Field>
        <Field label="Ends"><input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} /></Field>
      </div>
      <Field label="Description"><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={2000} /></Field>
      <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} /></Field>
      <button
        type="button"
        className="btn"
        disabled={name.trim().length === 0}
        onClick={() =>
          void onSave({
            name: name.trim(),
            kind,
            status,
            fund,
            fundingSource,
            description: description.trim() || null,
            budget: budget.trim() === '' ? null : Number(budget),
            specialAssessmentLevied: levied.trim() === '' ? null : Number(levied),
            specialAssessmentCollected: collected.trim() === '' ? null : Number(collected),
            startsOn: startsOn || null,
            endsOn: endsOn || null,
            vendorName: vendorName.trim() || null,
            notes: notes.trim() || null,
          })
        }
      >
        {project ? 'Save changes' : 'Add project'}
      </button>
    </div>
  );
}

function ProjectCard({
  project: p,
  kinds,
  fundingSources,
  open,
  onToggle,
  onSave,
  onDelete,
  onExpense,
  onExpenseDelete,
  onSchedule,
  onScheduleDone,
  onScheduleDelete,
}: {
  project: FinProject;
  kinds: ProjectKind[];
  fundingSources: FundingSource[];
  open: boolean;
  onToggle: () => void;
  onSave: (input: Record<string, unknown>) => void | Promise<void>;
  onDelete: () => void;
  onExpense: (input: Record<string, unknown>) => void | Promise<void>;
  onExpenseDelete: (id: string) => void | Promise<void>;
  onSchedule: (input: Record<string, unknown>) => void | Promise<void>;
  onScheduleDone: (id: string) => void | Promise<void>;
  onScheduleDelete: (id: string) => void | Promise<void>;
}) {
  const pct = p.budget && p.budget > 0 ? Math.min(100, (p.spent / p.budget) * 100) : null;
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3 style={{ margin: 0 }}>{p.name}</h3>
          <span className="mut">{KIND_LABEL[p.kind] ?? p.kind} · {p.fund === 'replacement' ? 'Replacement fund' : 'Operating fund'} · {FUNDING_LABEL[p.fundingSource] ?? p.fundingSource}</span>
        </div>
        <Tag tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Tag>
      </div>
      {p.description && <p className="mut">{p.description}</p>}
      <div className="row between">
        <span>Actual {usd(p.spent)}{p.budget !== null ? ` of ${usd(p.budget)} budgeted` : ''}</span>
        {p.remaining !== null && <span className={p.overBudget ? 'bad' : 'mut'}>{p.overBudget ? `${usd(-p.remaining)} over` : `${usd(p.remaining)} left`}</span>}
      </div>
      {pct !== null && <div className="bar-track"><div className="bar" style={{ width: `${pct}%`, background: p.overBudget ? 'var(--bad)' : undefined }} /></div>}
      <div className="stat-grid" style={{ marginTop: 8 }}>
        <Metric label="Special assessment outstanding" value={usd(p.specialAssessment?.outstanding ?? 0)} hint={`${usd(p.specialAssessment?.collected ?? 0)} of ${usd(p.specialAssessment?.levied ?? 0)} collected`} />
        <Metric label="Planned payments left" value={usd(p.forecast?.plannedPaymentsRemaining ?? 0)} hint={p.forecast?.nextDue ? `Next ${p.forecast.nextDue}` : 'None scheduled'} />
        <Metric label="Forecast cash impact remaining" value={usd(p.forecast?.netCashImpact ?? 0)} hint="Receipts still due minus payments still due" estimate />
      </div>
      <button type="button" className="btn s" onClick={onToggle}>{open ? 'Hide details' : 'Details'}</button>

      {open && (
        <div style={{ marginTop: 12 }}>
          <h4>Actual spending</h4>
          {p.expenses.length === 0 ? <p className="mut">Nothing recorded yet. Planned dates below are not actuals.</p> : (
            <div className="tw">
              <table>
                <tbody>
                  {p.expenses.map((e) => (
                    <tr key={e.id}>
                      <td className="nowrap">{e.spentOn ?? ''}</td>
                      <td>{e.vendorName ?? ''}<div className="mut">{e.description}{e.fromLedger ? ' · from ledger (already in statement expenses)' : ' · entered by hand'}</div></td>
                      <td className="num">{usd(e.amount, true)}</td>
                      <td className="num"><button type="button" className="btn tiny ghost" onClick={() => void onExpenseDelete(e.id)}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ExpenseAdder onExpense={onExpense} />

          <h4>Planned payment dates</h4>
          {(p.schedule ?? []).length === 0 ? <p className="mut">No dates yet. These feed the cash-flow forecast, not actual spending.</p> : (
            <div className="tw">
              <table>
                <tbody>
                  {(p.schedule ?? []).map((s) => (
                    <tr key={s.id}>
                      <td className="nowrap">{s.dueOn}</td>
                      <td>{s.direction === 'receipt' ? 'Receipt' : 'Payment'}{s.note ? ` · ${s.note}` : ''}</td>
                      <td className="num">{usd(s.amount, true)}</td>
                      <td><Tag tone={s.status === 'done' ? 'ok' : 'pend'}>{s.status}</Tag></td>
                      <td className="num">
                        <button type="button" className="btn tiny ghost" onClick={() => void onScheduleDone(s.id)}>{s.status === 'done' ? 'Mark planned' : 'Mark done'}</button>
                        <button type="button" className="btn tiny ghost" onClick={() => void onScheduleDelete(s.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ScheduleAdder onSchedule={onSchedule} />

          <details style={{ marginTop: 12 }}>
            <summary>Edit project</summary>
            <ProjectForm kinds={kinds} fundingSources={fundingSources} project={p} onSave={onSave} />
          </details>
          <button type="button" className="btn s danger" style={{ marginTop: 8 }} onClick={onDelete}>Delete project</button>
        </div>
      )}
    </div>
  );
}

function ScheduleAdder({ onSchedule }: { onSchedule: (input: Record<string, unknown>) => void | Promise<void> }) {
  const [direction, setDirection] = useState<'payment' | 'receipt'>('payment');
  const [dueOn, setDueOn] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  return (
    <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
      <Field label="Kind">
        <select value={direction} onChange={(e) => setDirection(e.target.value as 'payment' | 'receipt')}>
          <option value="payment">Payment</option>
          <option value="receipt">Receipt</option>
        </select>
      </Field>
      <Field label="Date"><input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} /></Field>
      <Field label="Amount ($)"><input value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Note"><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <button
        type="button"
        className="btn s"
        disabled={!dueOn || Number(amount) <= 0}
        onClick={async () => {
          await onSchedule({ direction, dueOn, amount: Number(amount), note: note.trim() || null });
          setAmount(''); setNote('');
        }}
      >
        Add date
      </button>
    </div>
  );
}

function ExpenseAdder({ onExpense }: { onExpense: (input: Record<string, unknown>) => void | Promise<void> }) {
  const [mode, setMode] = useState<'none' | 'manual' | 'ledger'>('none');
  const [spentOn, setSpentOn] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const candidates = useAsync(async () => (mode === 'ledger' ? (await api.finProjectCandidates()).entries : []), [mode]);

  return (
    <div style={{ marginTop: 8 }}>
      <div className="btn-row">
        <button type="button" className="btn s" onClick={() => setMode(mode === 'ledger' ? 'none' : 'ledger')}>Link a ledger transaction</button>
        <button type="button" className="btn s" onClick={() => setMode(mode === 'manual' ? 'none' : 'manual')}>Record spending by hand</button>
      </div>
      {mode === 'manual' && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
          <Field label="Date"><input type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} /></Field>
          <Field label="Vendor"><input value={vendorName} onChange={(e) => setVendorName(e.target.value)} /></Field>
          <Field label="What for"><input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          <Field label="Amount ($)"><input value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} /></Field>
          <button
            type="button"
            className="btn"
            disabled={amount.trim() === '' || Number.isNaN(Number(amount))}
            onClick={async () => {
              await onExpense({ spentOn: spentOn || null, vendorName: vendorName.trim() || null, description: description.trim() || null, amount: Number(amount) });
              setAmount(''); setDescription(''); setVendorName(''); setSpentOn('');
            }}
          >
            Add
          </button>
        </div>
      )}
      {mode === 'ledger' && (
        <div className="tw" style={{ marginTop: 8 }}>
          {candidates.loading ? <p className="mut">Loading ledger entries…</p> : (candidates.data ?? []).length === 0 ? (
            <p className="mut">No unassigned project-like entries in the latest approved statement.</p>
          ) : (
            <table>
              <tbody>
                {(candidates.data as ProjectCandidate[]).map((c) => (
                  <tr key={c.id}>
                    <td className="nowrap">{c.postedOn ?? ''}</td>
                    <td>{c.vendor ?? ''}<div className="mut">{c.description} · {c.account}</div></td>
                    <td className="num">{usd(c.amount, true)}</td>
                    <td className="num"><button type="button" className="btn tiny" onClick={async () => { await onExpense({ glEntryId: c.id }); await candidates.reload(); }}>Link</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
